import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  Injector,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { IdName } from 'src/app/shared/models/id-name';
import { MenuTreeStructureComponent } from 'src/app/shared/menu-tree-module/menu-tree-structure/menu-tree-structure.component';
import { MOBILE_LAYOUT_QUERIES } from 'src/app/shared/constants/responsive-breakpoints';
import { isDepartmentPaused } from 'src/app/shared/user-view/functions/is-department-paused';
import { ITenantUserAccess } from 'src/app/shared/user-view/models/tenant-user-access.model';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantSelectorComponent } from 'src/app/tenant-selector/tenant-selector.component';
import { AppUpdateService } from 'src/app/services/app-update.service';
import { TextControlService } from 'src/app/shared/text-control-module/text-control.service';
import { environment } from 'src/environments/environment';
import { LoadTenantUserAccess } from '../../../tenant-selector/state/tenant-user-access.actions';
import { MenuListState } from '../../state/menu-list.state';
import { UiState } from '../../state/ui.state';
import {
  FirstVisitHintIds,
  FirstVisitHintService,
} from '../../../shared/services/first-visit-hint.service';
import {
  DEFAULT_MOBILE_DETAIL_VIEW_SHELL_STATE,
  MobileDetailViewShellService,
} from '../../../shared/services/mobile-detail-view-shell.service';
import { getAdminRoutesWithChildren } from '../menu-list/menu-list-routes';
import { TenantIdListComponent } from '../tenant-id-list/tenant-id-list.component';
import { ToastService } from '../../services/toast.service';
import { Animations, AnimationDurations } from '../../angular-animations/angular-animations';
import { DepartmentSelectorMobileComponent } from '../department-selector-mobile/department-selector-mobile.component';
import { UserProfileDropdownMobileComponent } from '../user-profile-dropdown-mobile/user-profile-dropdown-mobile.component';
import {
  AdminRoutesEnum,
  RoutesEnum,
  UserDocumentsRoutes,
  UserNonConformancesRoutes,
  UserOrderRoutes,
} from '../../enums/routes.enum';
import { SidenavService } from '../../services/side-nav.service';
import { ToggleToolbar } from '../../state/ui.actions';
import { CartModalComponent } from '../../../storage-user/modals/cart-modal/cart-modal.component';
import { CartState } from '../../../storage-user/state/cart.state';

const USER_ROUTES_WITH_INNER_NAV = [
  RoutesEnum.Certificate,
  RoutesEnum.Documents,
  RoutesEnum.Maintenance,
  RoutesEnum.Storage,
  RoutesEnum.Medicines,
  RoutesEnum.Drills,
  RoutesEnum.Checklists,
  RoutesEnum.RiskAssessments,
  AdminRoutesEnum.Users,
  RoutesEnum.Users,
];
const URLS_WITH_HIDDEN_OVERFLOW_ON_TOOLBAR_ANIMATION: string[] = [
  UserNonConformancesRoutes.Open,
  UserNonConformancesRoutes.Completed,
  UserNonConformancesRoutes.Closed,
  UserNonConformancesRoutes.Deleted,
  UserDocumentsRoutes.Published,
  UserDocumentsRoutes.Draft,
  UserDocumentsRoutes.ForApproval,
  UserDocumentsRoutes.Rejected,
  UserDocumentsRoutes.Disabled,
  UserDocumentsRoutes.Deleted,
  UserDocumentsRoutes.UnderRevision,
  UserDocumentsRoutes.ChangeRequests,
  UserOrderRoutes.Dashboard,
  UserOrderRoutes.Draft,
  UserOrderRoutes.ForApproval,
  UserOrderRoutes.Approved,
  UserOrderRoutes.SentToSupplier,
  UserOrderRoutes.ReceivedPartially,
  UserOrderRoutes.Received,
  UserOrderRoutes.Cancelled,
];

@Component({
  selector: 'app-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss'],
  animations: [Animations.slideDown],
  standalone: false,
})
export class MainNavComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly injector = inject(Injector);
  private readonly mobileDetailViewShell = inject(MobileDetailViewShellService);
  private readonly firstVisitHint = inject(FirstVisitHintService);
  readonly menuStructureHintId = FirstVisitHintIds.menuStructure;

  @ViewChild('innerNavToggleButton', { read: ElementRef }) innerNavToggleButton: ElementRef;
  @ViewChild('innerNavContent', { read: ElementRef }) innerNavContent: ElementRef;
  @ViewChild('drawer', { read: ElementRef }) mainSidenavEl: ElementRef;
  @ViewChild('drawer', { read: MatSidenav }) mainSidenav: MatSidenav;
  @ViewChild('inner', { read: MatSidenav }) innerMenu: MatSidenav;
  @ViewChild(MenuTreeStructureComponent) structureMenu?: MenuTreeStructureComponent;

  adminInnerModuleName = select(MenuListState.getAdminInnerNavModuleName);
  isDarkMode = select(UiState.isDarkMode);
  currentDepartmentId = select(TenantUserAccessState.currentDepartmentId);
  private readonly currentDepartmentChanges$ = toObservable(this.currentDepartmentId, {
    injector: this.injector,
  });

  readonly isHandset = toSignal(
    this.breakpointObserver.observe(MOBILE_LAYOUT_QUERIES).pipe(
      map((result: BreakpointState) => result.matches),
      shareReplay({ bufferSize: 1, refCount: true })
    ),
    { initialValue: this.breakpointObserver.isMatched(MOBILE_LAYOUT_QUERIES) }
  );
  readonly mobileDetailViewState = toSignal(this.mobileDetailViewShell.state$, {
    initialValue: DEFAULT_MOBILE_DETAIL_VIEW_SHELL_STATE,
  });

  selectedDepartment = select(TenantUserAccessState.currentDepartment);
  cartCount = select(CartState.getCount);
  allAvailableDepartments = select(TenantUserAccessState.getAllAvailableDepartments);
  sortedDepartments = computed(() =>
    [...(this.allAvailableDepartments() ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  );
  userAccess = select(TenantUserAccessState.userAccess);
  isToolbarCollapsed = select(UiState.isToolbarCollapsed);

  readonly ALL_DEPS_TOKEN = ALL_DEPS_TOKEN;
  readonly isDepartmentPaused = isDepartmentPaused;
  allDepsIconPath = '/assets/icons/all-departments.svg';
  departmentPlaceHolderIconPath = '/assets/icons/department-placeholder.svg';
  innerMenuOpened = false;
  showSecondarySidenav = false;
  showCartButton = false;
  isInAdmin: boolean;
  depId: string;
  desktopMode: boolean;
  url = environment.textControlWS;
  forceReload = false;
  isToolbarAnimating = false;

  hideScrollOnToolbarAnimationInModule = false;

  get showMobileStructureToggle(): boolean {
    return (
      !this.desktopMode &&
      this.showSecondarySidenav &&
      !this.mobileDetailViewState().hideInnerNavToggle
    );
  }

  showStructureHint(): boolean {
    return (
      this.showMobileStructureToggle && this.firstVisitHint.shouldShowHint(this.menuStructureHintId)
    );
  }

  get useTextControl(): boolean {
    return TextControlService.isTextControlEnabled;
  }

  private firstRun = true;
  private subscription = new Subscription();
  private currentModule?: string;
  private toolbarAnimationTimeout?: number;

  constructor(
    public textControl: TextControlService,
    private appUpdateService: AppUpdateService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private store: Store,
    private translate: TranslateService,
    private renderer: Renderer2,
    private toastService: ToastService,
    private sidenavService: SidenavService
  ) {
    effect(() => {
      this.mobileDetailViewState();
      this.syncStructureHintRegistration();
    });
  }

  ngOnInit(): void {
    this.desktopMode = !this.breakpointObserver.isMatched(MOBILE_LAYOUT_QUERIES);
    this.listenToRouteChanges();
    this.processURL(this.router.url);
    this.syncStructureHintRegistration();

    this.subscription.add(
      this.breakpointObserver.observe(MOBILE_LAYOUT_QUERIES).subscribe((state: BreakpointState) => {
        const desktopMode = !state.matches;
        if (desktopMode !== this.desktopMode) {
          this.desktopMode = desktopMode;
          this.innerMenuOpened = this.desktopMode && this.showSecondarySidenav;
          this.syncStructureHintRegistration();
        }
        if (this.firstRun) {
          this.getSelectors();
          this.firstRun = false;
        }
      })
    );

    this.subscription.add(
      this.currentDepartmentChanges$.subscribe((depId: string) => {
        this.depId = depId;
      })
    );

    this.subscription.add(
      this.textControl.forceReload.subscribe(() => {
        this.forceReload = true;
        setTimeout(() => {
          this.forceReload = false;
        }, 100);
      })
    );
  }

  ngAfterViewInit(): void {
    this.bindInnerNavToggleButton();
    this.sidenavService.setSidenav(this.mainSidenav);
  }

  ngOnDestroy(): void {
    if (this.toolbarAnimationTimeout) {
      clearTimeout(this.toolbarAnimationTimeout);
    }
    this.firstVisitHint.unregisterHint(this.menuStructureHintId);
    this.subscription.unsubscribe();
    this.textControl.dispose();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.textControl.dispose();
  }

  bindInnerNavToggleButton(): void {
    const callback: MutationCallback = (mutationsList: MutationRecord[]) => {
      mutationsList.forEach(({ type }) => {
        if (type === 'attributes' && this.desktopMode) {
          this.setSecondarySidenavButtonOffset();
        }
      });
    };

    const observer = new MutationObserver(callback);
    observer.observe(this.innerNavContent.nativeElement, { attributes: true });
  }

  async toggleMainNav(): Promise<void> {
    if (!this.desktopMode) {
      if (!this.mainSidenav.opened) {
        this.innerMenuOpened = false;
      }
      await this.sidenavService.toggle();
      return;
    }
    await this.sidenavService.toggle();
    this.setSecondarySidenavButtonOffset();
  }

  setSecondarySidenavButtonOffset(): void {
    if (!this.innerNavToggleButton?.nativeElement) {
      return;
    }
    const baseOffset = this.getMainSidenavOffset();
    const newContentOffset = this.innerNavContent.nativeElement.style.marginLeft;
    let newButtonOffset = baseOffset;
    if (newContentOffset) {
      newButtonOffset += +newContentOffset.replace('px', '');
    }
    this.renderer.setStyle(
      this.innerNavToggleButton.nativeElement,
      'margin-left',
      `${newButtonOffset}px`
    );
  }

  private getMainSidenavOffset(): number {
    return this.mainSidenav.opened
      ? this.mainSidenavEl.nativeElement.getBoundingClientRect().width
      : 0;
  }

  openInnerMenu(open?: boolean, url?: string, isAdmin?: boolean): void {
    if (open !== undefined && !this.isInCurrentModule(url, isAdmin)) {
      this.innerMenuOpened = open;
    } else if (!this.isInCurrentModule(url, isAdmin)) {
      this.toggleInnerMenu();
    }
  }

  toggleInnerMenu(): void {
    this.dismissStructureHint();
    const shouldOpen = !this.innerMenuOpened;
    if (!this.desktopMode && shouldOpen && this.mainSidenav.opened) {
      this.sidenavService.close();
    }
    this.innerMenuOpened = shouldOpen;
  }

  closeInnerMenuOnMobile(): void {
    if (!this.desktopMode) {
      this.innerMenuOpened = false;
    }
  }

  dismissStructureHint(): void {
    this.firstVisitHint.dismissHint(this.menuStructureHintId);
  }

  toggleToolbar(): void {
    if (this.toolbarAnimationTimeout) {
      clearTimeout(this.toolbarAnimationTimeout);
    }
    // Block overflow while animating to avoid horizontal scrollbar
    this.isToolbarAnimating = true;
    this.store.dispatch(new ToggleToolbar(!this.store.selectSnapshot(UiState.isToolbarCollapsed)));
    this.toolbarAnimationTimeout = setTimeout(() => {
      this.isToolbarAnimating = false;
    }, AnimationDurations.TOOLBAR_SLIDE + 100) as unknown as number;
  }

  goToCCOMDocs(): void {
    window.open('https://support.ccom.no/portal/en/kb/ccom', '_blank');
  }

  openDepartmentSelectorMobile(): void {
    this.dialog.open(DepartmentSelectorMobileComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      autoFocus: false,
    });
  }

  openUserProfileMobileDialog(): void {
    this.dialog.open(UserProfileDropdownMobileComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      autoFocus: false,
    });
  }

  openCartDialog(): void {
    this.dialog.open(CartModalComponent, {
      width: '52rem',
      maxWidth: '90vw',
      panelClass: ['no-padding', 'mobile-sizing-for-mat-dialogs'],
    });
  }

  private listenToRouteChanges(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.processURL(event.url);
        this.closeNavigationMenusOnMobile();
      });
  }

  private closeNavigationMenusOnMobile(): void {
    if (this.desktopMode) {
      return;
    }
    if (this.mainSidenav?.opened) {
      this.sidenavService.close();
    }
    this.innerMenuOpened = false;
  }

  private isInCurrentModule(url?: string, isAdmin?: boolean): boolean {
    const currentModule = isAdmin ? url?.split('/')[2] : url?.split('/')[1];
    const currentPath = this.getUrlPath(this.router.url);
    this.hideScrollOnToolbarAnimationInModule = URLS_WITH_HIDDEN_OVERFLOW_ON_TOOLBAR_ANIMATION.some(
      (_url) => currentPath.endsWith(_url)
    );
    if (currentModule !== this.currentModule) {
      this.currentModule = currentModule;
      return false;
    }
    return true;
  }

  private processURL(url: string): void {
    this.showCartButton = this.isCartModuleUrl(url);
    const urlPath = this.getUrlPath(url);
    const urlFragments = this.getUrlFragments(urlPath);
    this.isInAdmin = urlFragments.includes(AdminRoutesEnum.Admin);
    if (this.isInAdmin) {
      this.showSecondarySidenav = getAdminRoutesWithChildren().some((route) =>
        urlPath.includes(this.getRoutePath(route))
      );
    } else {
      this.showSecondarySidenav = USER_ROUTES_WITH_INNER_NAV.some((moduleName) =>
        urlFragments.includes(moduleName.toLowerCase())
      );
    }
    this.openInnerMenu(this.desktopMode && this.showSecondarySidenav, urlPath, this.isInAdmin);
    this.syncStructureHintRegistration();
  }

  private syncStructureHintRegistration(): void {
    if (this.showMobileStructureToggle) {
      this.firstVisitHint.registerHint(this.menuStructureHintId);
      return;
    }

    this.firstVisitHint.unregisterHint(this.menuStructureHintId);
  }

  private getUrlPath(url: string): string {
    return url.split(/[?#]/)[0];
  }

  private getUrlFragments(url: string): string[] {
    return url
      .split('/')
      .filter((fragment) => !!fragment)
      .map((fragment) => fragment.toLowerCase());
  }

  private getRoutePath(route: string): string {
    return route.startsWith('/') ? route : `/${route}`;
  }

  private isCartModuleUrl(url: string): boolean {
    const firstSegment = url.split(/[?#]/)[0].split('/').filter(Boolean)[0];
    return firstSegment === RoutesEnum.Storage || firstSegment === RoutesEnum.Purchase;
  }

  private getSelectors(): void {
    if (!this.appUpdateService.isPopupShown) {
      this.subscription.add(
        this.store
          .select(TenantUserAccessState.getAllAvailableTenants)
          .pipe(take(1))
          .subscribe((tenants: ITenantUserAccess[]) => {
            if (tenants && tenants.length > 1) {
              const selectedTenantId = TenantUserAccessState.getLastSelectedTenantId();
              if (!selectedTenantId) {
                this.dialog
                  .open(TenantSelectorComponent, {
                    width: this.desktopMode ? '50rem' : '100vw',
                    height: this.desktopMode ? '' : '100vh',
                    maxWidth: this.desktopMode ? '80vw' : '100vw',
                    autoFocus: false,
                  })
                  .afterClosed()
                  .subscribe();
              }
            } else if (tenants && tenants.length === 0) {
              this.toastService.error('userHasNoActiveTenants');
            }
          })
      );
    }
  }

  private openDialog(tenants: IdName[]): void {
    const title = this.translate.instant('chooseATenant');

    const dialogRef = this.dialog.open(TenantIdListComponent, {
      width: '250px',
      data: { title, tenants },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      const tenantId = result.data;
      if (tenantId !== getTenantId()) {
        this.store.dispatch(new LoadTenantUserAccess(tenantId));
      }
    });
  }
}
